import { useMemo } from 'react';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { evaluationQueries } from '@/data/query-keys/evaluationQueryKey';
import {
  applyTemplate,
  deleteEvaluation,
} from '@/data/services/evaluationServices';
import Evaluation, { EvaluationTypeEnum } from '@/models/Evaluation';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import useListService from '../useListService';
import { useMasterSlaveMap } from './useMasterSlaveMap';

type UseEvaluationSlavesProps = {
  courseLineId: number;
  masters: Evaluation[];
};

const useSlaveEvaluations = ({
  courseLineId,
  masters,
}: UseEvaluationSlavesProps) => {
  const templateIds = useMemo(() => masters.map(e => e.id), [masters]);

  const {
    results: slaves,
    isInitialLoading: isLoadingSlaves,
    cacheManager,
  } = useListService({
    ...evaluationQueries.list({
      type: EvaluationTypeEnum.EVALUATION,
      sourceTemplateIds: templateIds,
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled: templateIds.length > 0,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const { slavesByMaster, isLoading: isMapLoading } = useMasterSlaveMap({
    courseLineId,
    masters,
    slaves,
  });

  const syncCoursePaths = async (
    templateId: number,
    targetCoursePathIds: number[],
  ) => {
    const appliedEntries = slavesByMaster.get(templateId) ?? [];

    const toApply = targetCoursePathIds.filter(
      id => !appliedEntries.some(entry => entry.coursePath.id === id),
    );
    const toRemove = appliedEntries.filter(
      entry => !targetCoursePathIds.includes(entry.coursePath.id ?? -1),
    );

    const replacedSlaves = slaves.filter(
      slave =>
        slave.coursePathId != null && toApply.includes(slave.coursePathId),
    );

    try {
      const [appliedSlaves] = await Promise.all([
        Promise.all(
          toApply.map(coursePathId =>
            applyTemplate({ templateId, coursePathId }),
          ),
        ),
        Promise.all(toRemove.map(entry => deleteEvaluation(entry.slave.id))),
      ]);

      cacheManager.batch([
        ...replacedSlaves.map(slave => () => cacheManager.remove(slave.id)),
        ...appliedSlaves.map(slave => () => cacheManager.add(slave)),
        ...toRemove.map(entry => () => cacheManager.remove(entry.slave.id)),
      ]);

      return { applied: toApply.length, removed: toRemove.length };
    } catch (err) {
      alert.error(getErrorMessage(err));
      throw err;
    }
  };

  const isLoading = isMapLoading || isLoadingSlaves;

  return {
    syncCoursePaths,
    isLoading,
    slaves,
    cacheManager,
    slavesByMaster,
  };
};

export default useSlaveEvaluations;
